// Describes which popup showAlertOrIncidentPopup() should open. The component opens it.

export const ALERT_ACTION_TYPE = {
    NOOP: 'noop',
    ALERT_POPUP: 'alertPopup',
    INCIDENT_POPUP: 'incidentPopup'
}

export function isEmailFirstTrigger(alertsTriggers) {
    return Boolean(alertsTriggers && alertsTriggers[0] && alertsTriggers[0].type === 'email')
}

export function collectNonEmailTriggerMessages(alertsTriggers) {
    const messages = []

    if (!alertsTriggers || !alertsTriggers.length) {
        return messages
    }

    alertsTriggers.forEach((trigger) => {
        if (!trigger || trigger.type === 'email' || (!trigger.msg && !trigger.message)) {
            return
        }

        const msg = trigger.msg || trigger.message
        if (msg && !messages.includes(msg)) {
            messages.push(msg)
        }
    })

    return messages
}

// Audits without alerts.message join non-email trigger text. Other tools use alerts.message.
export function buildAlertMessage(alerts, alertsTriggers, isAudit) {
    const fallback = {
        text: (alerts && alerts.message) || null,
        mood: (alerts && alerts.class) || 'surprised'
    }

    if (!isAudit) {
        return fallback
    }

    if (alerts && alerts.message) {
        return {
            text: alerts.message,
            mood: alerts.class || 'surprised'
        }
    }

    const messages = collectNonEmailTriggerMessages(alertsTriggers)
    if (messages.length === 0) {
        return fallback
    }

    return {
        text: messages.join('<br/>'),
        mood: fallback.mood
    }
}

function resolveIncidentTemplates(alerts, canUseIncidentTool, incidentsTemplates) {
    const matchingTemplateIds = (alerts && alerts.issues) || []
    const templates = incidentsTemplates || {}
    const incidentTemplateIds = canUseIncidentTool ? matchingTemplateIds.filter((tplId) => Boolean(templates[tplId])) : []

    return {
        incidentTemplateIds,
        hasIncidentTemplates: incidentTemplateIds.length > 0,
        hasLinkedTemplateIds: matchingTemplateIds.length > 0
    }
}

function isEmailWithLinkedTemplatesAndIncidentsOff(emailFirst, canUseIncidentTool, hasLinkedTemplateIds) {
    return Boolean(emailFirst && !canUseIncidentTool && hasLinkedTemplateIds)
}

function hasSeparateAlert(alerts, alertsTriggers) {
    const nonEmailTriggers = (alertsTriggers || []).filter((trigger) => trigger && trigger.type !== 'email')
    if (nonEmailTriggers.length === 0) {
        return false
    }

    if (alerts && alerts.message) {
        return true
    }

    return nonEmailTriggers.some((trigger) => trigger.msg || trigger.message)
}

function shouldSkipEmailOnly(emailFirst, hasIncidentTemplates, canUseIncidentTool, hasLinkedTemplateIds, alerts, alertsTriggers) {
    if (!emailFirst) {
        return false
    }

    if (hasIncidentTemplates) {
        return false
    }

    if (isEmailWithLinkedTemplatesAndIncidentsOff(emailFirst, canUseIncidentTool, hasLinkedTemplateIds)) {
        return false
    }

    if (hasSeparateAlert(alerts, alertsTriggers)) {
        return false
    }

    return true
}

function shouldShowAlertPopup(alerts, emailFirst, hasIncidentTemplates, canUseIncidentTool, hasLinkedTemplateIds, alertsTriggers) {
    if (!alerts || alerts.message === '') {
        return false
    }

    return !shouldSkipEmailOnly(emailFirst, hasIncidentTemplates, canUseIncidentTool, hasLinkedTemplateIds, alerts, alertsTriggers)
}

// Returns a plain object describing which popup to open. It does not open a popup.
export function resolveItemAlertAction(context) {
    const alerts = context.alerts || null
    const alertsTriggers = context.alertsTriggers || null

    if (!alerts && !alertsTriggers) {
        return { type: ALERT_ACTION_TYPE.NOOP }
    }

    const canUseIncidentTool = Boolean(context.canUseIncidentTool)
    const emailFirst = isEmailFirstTrigger(alertsTriggers)
    const templates = resolveIncidentTemplates(alerts, canUseIncidentTool, context.incidentsTemplates)
    const built = buildAlertMessage(alerts, alertsTriggers, context.isAudit)

    if (shouldSkipEmailOnly(emailFirst, templates.hasIncidentTemplates, canUseIncidentTool, templates.hasLinkedTemplateIds, alerts, alertsTriggers)) {
        return { type: ALERT_ACTION_TYPE.NOOP }
    }

    if (context.isAudit && alerts && built.text && !templates.hasIncidentTemplates) {
        return {
            type: ALERT_ACTION_TYPE.ALERT_POPUP,
            mood: built.mood,
            message: built.text
        }
    }

    if (context.isNonConformity) {
        return { type: ALERT_ACTION_TYPE.NOOP }
    }

    if (templates.hasIncidentTemplates && !context.isActionPlanItem) {
        return {
            type: ALERT_ACTION_TYPE.INCIDENT_POPUP,
            mood: built.mood,
            message: built.text,
            templateIds: templates.incidentTemplateIds,
            hasExistingIncident: Boolean(context.hasExistingIncident)
        }
    }

    if (shouldShowAlertPopup(alerts, emailFirst, templates.hasIncidentTemplates, canUseIncidentTool, templates.hasLinkedTemplateIds, alertsTriggers)) {
        return {
            type: ALERT_ACTION_TYPE.ALERT_POPUP,
            mood: alerts.class,
            message: alerts.message
        }
    }

    return { type: ALERT_ACTION_TYPE.NOOP }
}

// Online, checklist/add returns { [id]: incident }. Offline, issues/add returns { id }.
export function readCreatedIncidentId(response) {
    if (!response || typeof response !== 'object') {
        return null
    }

    if (typeof response.id === 'string' || typeof response.id === 'number') {
        return response.id
    }

    const firstKey = Object.keys(response)[0]
    return firstKey || null
}
